// Cuadrito del producto: la foto que subió la distribuidora o, si no hay, un ícono según lo que es (agua, cerveza,
// gaseosa, vino, hielo, limpieza…). Se busca por palabras, sin tildes, primero en el nombre (más preciso: "Carbón
// vegetal" dentro de "Hielo y varios") y después en la categoría; si nada coincide, una caja. Las fotos se piden
// chicas y cuando están por verse, y aparecen con un fundido corto (useFundido).
import { normalizarTexto } from '../compartido';
import { cx, Icono } from '@reparto/ui';
import { useCallback, useState } from 'react';

/**
 * Una foto que aparece con un fundido corto al terminar de cargar, en lugar de saltar: mientras llega, su cuadro es un
 * esqueleto (`esperando`, para el contenedor). Si ya estaba en el navegador se ve enseguida, sin fundido; sin
 * movimiento (prefers-reduced-motion), aparece sin fundido. `props` van en el <img> y `clase`, en su className.
 */
export function useFundido(src: string | null | undefined) {
  const [cargada, setCargada] = useState<string | null>(null);
  const lista = !!src && cargada === src;
  // La foto que ya estaba (en memoria o en la caché) puede estar completa antes de pintarse: se muestra sin esperar.
  const ref = useCallback(
    (img: HTMLImageElement | null) => {
      if (src && img?.complete && img.naturalWidth > 0) setCargada(src);
    },
    [src],
  );
  return {
    lista,
    esperando: !!src && !lista,
    props: { ref, onLoad: () => setCargada(src ?? null) },
    clase: cx(
      'transition-opacity duration-200 ease-out motion-reduce:transition-none',
      lista ? 'opacity-100' : 'opacity-0',
    ),
  };
}

// Ícono de Material Symbols y las palabras que lo eligen (cuentan desde el comienzo de la palabra: "cerveza" también
// encuentra "cervezas"; con punto al final, solo la palabra entera: "te." no encuentra "tetra"). El orden importa: gana
// la primera que aparece ("agua tónica" es gaseosa).
const TABLA =
  'water_bottle gaseosa refresco cola tonica guarana soda,bolt energizante energetica isotonica deportiva,' +
  'sports_bar cerveza chopp porron barril,wine_bar vino espumante sidra champa,' +
  'liquor aperitivo fernet cana. whisky vodka licor ron. gin. aguardiente,nutrition jugo nectar zumo,' +
  'water_drop agua mineral,ac_unit hielo cubito,local_fire_department carbon. lena.,glass_cup vaso copa taza termico,' +
  'coffee cafe. te. mate. cocido,local_bar pajita sorbete bombilla,restaurant cubierto cuchara tenedor cuchillo,' +
  'dinner_dining plato bandeja,shopping_bag bolsa,' +
  'cleaning_services limpieza detergente lavandina jabon desinfectante balde trapo escoba,' +
  'receipt servilleta papel bobina higienico,layers film aluminio rollo,celebration fiesta cumple globo vela,' +
  'takeout_dining envase pote bidon frasco descartable,cookie galletita golosina caramelo chocolate snack papas,' +
  'water_full lacteo leche yogur queso,bakery_dining pan. panaderia galleta.,' +
  'grocery almacen aceite arroz fideo harina azucar yerba conserva,' +
  'icecream congelado helado,local_drink bebida';
const ICONOS = TABLA.split(',').map((fila) => {
  const [icono, ...palabras] = fila.split(' ');
  return [new RegExp(`\\b(${palabras.join('|').replace(/\./g, '\\b')})`), icono!] as const;
});
/** Cuando nada coincide. */
export const ICONO_GENERICO = 'inventory_2';

export function iconoDeProducto(categoria: string | null | undefined, nombre?: string | null): string {
  for (const texto of [nombre, categoria]) {
    if (!texto) continue;
    const t = normalizarTexto(texto);
    const hallado = ICONOS.find(([patron]) => patron.test(t));
    if (hallado) return hallado[1];
  }
  return ICONO_GENERICO;
}

export function Miniatura({
  nombre,
  categoria,
  foto,
  fotoMini,
  tamano = 'md',
  apagada,
  adaptable,
}: {
  nombre: string | null;
  categoria: string | null | undefined;
  /** Foto de hasta 480 px (para la grilla) y su miniatura de 160 px (para las listas). */
  foto?: string | null;
  fotoMini?: string | null;
  tamano?: 'sm' | 'md';
  apagada?: boolean;
  /** En la grilla (tablet y computadora) ocupa el ancho de la tarjeta. */
  adaptable?: boolean;
}) {
  const chica = fotoMini ?? foto;
  const fundido = useFundido(chica);
  return (
    <span
      aria-hidden="true"
      className={cx(
        'relative grid shrink-0 place-items-center overflow-hidden rounded-2xl',
        tamano === 'md' ? 'h-14 w-14 [--icono:30px]' : 'h-12 w-12 [--icono:26px]',
        adaptable && '@xl:aspect-[4/3] @xl:h-auto @xl:w-full @xl:[--icono:52px]',
        chica ? 'border border-stone-200' : 'bg-stone-100',
        chica && (fundido.esperando ? 'esqueleto bg-stone-200' : 'bg-[#fff]'),
        apagada ? 'text-stone-500' : 'text-primary-700',
        chica && apagada && 'opacity-50',
      )}
    >
      {chica ? (
        <img
          src={chica}
          srcSet={foto && fotoMini && foto !== fotoMini ? `${fotoMini} 160w, ${foto} 480w` : undefined}
          sizes={adaptable ? '(min-width: 36rem) 320px, 56px' : '56px'}
          width={160}
          height={160}
          loading="lazy"
          decoding="async"
          alt=""
          {...fundido.props}
          className={cx('absolute inset-0 h-full w-full object-contain p-1.5', fundido.clase)}
        />
      ) : (
        <Icono nombre={iconoDeProducto(categoria, nombre)} style={{ fontSize: 'var(--icono)' }} />
      )}
    </span>
  );
}
